import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  LayoutDashboard, FilePlus2, ListChecks, Globe2, ShoppingCart, Banknote, Ship, ShieldCheck, Boxes, Truck, CalendarDays, Search, MessagesSquare, Users as UsersIcon, LogOut, Menu, X, Settings as SettingsIcon, ClipboardList, Gauge, IndianRupee, Calculator, Receipt, ArrowLeftRight } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth, can } from '../lib/auth';
import { cn } from '../lib/cn';
import BrandMark from './BrandMark';
import '../styles/procurement-skin.css';
import IndentDetailModal from '../pages/procurement/IndentDetailModal';
import SwitchRealmButton from './SwitchRealmButton';
import { PROC_PAGES, isProcPageAllowed } from '../lib/procurementPages';
import EnquiryReminderPopup from './EnquiryReminderPopup';

// Route path → grantable page (drives the allowedPages sidebar filter).
const PROC_PAGE_BY_PATH = Object.fromEntries(PROC_PAGES.map((p) => [p.path, p]));

/**
 * Sidebar shell for the Procurement Portal — third walled-garden realm
 * alongside Spares and IMS Containers. Served on its own subdomain
 * (purchase.crystalgrp.xyz) but rendered from the same React bundle:
 *   • Realm guard in App.tsx → Protected bounces non-procurement users out.
 *   • Backend realm gate in middleware/auth.ts → 403s cross-realm API calls.
 *
 * Sidebar links are role-filtered against `user.procurementRole`:
 *   admin     — every link visible
 *   purchaser — indents, queues, vendors, dashboard
 *   hod       — approvals + dashboard
 *   accounts  — finance + dashboard
 *   logistics — logistics + dashboard
 *   depot     — depot screens + dashboard
 *   vendor    — (reserved; vendors normally come in via tokenised link only)
 */

interface NavItem {
  to: string;
  label: string;
  // Lucide icons are forwardRef components — using `any` here so the
  // refined `size?: number` prop signature doesn't conflict with their
  // wider LucideProps type.
  icon: any;
  /** Sub-roles allowed to see this link. Admins ignore this filter. */
  roles: Array<'purchaser' | 'hod' | 'accounts' | 'logistics' | 'depot'>;
}

/**
 * Who sees the P&L link. Mirrors the server's PROCUREMENT_PNL_EMAILS default.
 *
 * A courtesy only — the server refuses everyone else with a 404 regardless of
 * what this says, so the two drifting means at worst a link that leads to a
 * "not available" page, never a leak.
 */
const PNL_EMAILS = ['aa@crystalgroup.in'];

const NAV: NavItem[] = [
  { to: '/procurement',                 label: 'Dashboard',       icon: LayoutDashboard, roles: ['purchaser', 'hod', 'accounts', 'logistics', 'depot'] },
  { to: '/procurement/indents/new',     label: 'Create Indent',   icon: FilePlus2,       roles: ['purchaser'] },
  { to: '/procurement/indents',         label: 'All Indents',     icon: ListChecks,      roles: ['purchaser', 'hod', 'accounts', 'logistics', 'depot'] },
  { to: '/procurement/domestic',        label: 'Domestic Queue',  icon: ShoppingCart,    roles: ['purchaser'] },
  { to: '/procurement/import',          label: 'Import Queue',    icon: Globe2,          roles: ['purchaser'] },
  { to: '/procurement/sales-enquiries', label: 'Sales Enquiries', icon: MessagesSquare,  roles: ['purchaser'] },
  { to: '/procurement/finance',         label: 'Finance Desk',    icon: Banknote,        roles: ['accounts'] },
  { to: '/procurement/pi-bills',        label: 'PI → Finance',    icon: Receipt,         roles: ['purchaser', 'hod', 'accounts'] },
  { to: '/procurement/gate-portal',     label: 'Gate In / Out',   icon: ArrowLeftRight,  roles: ['purchaser', 'logistics', 'depot'] },
  { to: '/procurement/container-costs', label: 'Container Costs', icon: Calculator,    roles: ['purchaser', 'accounts'] },
  // Logistics Desk is hidden from the Purchase sidebar per request — not
  // needed day-to-day. The /procurement/logistics route still resolves for
  // anyone holding a direct link; it is simply no longer advertised here.
  { to: '/procurement/vessel-tracker',  label: 'Vessel Tracker',  icon: Ship,            roles: ['purchaser', 'hod', 'accounts', 'logistics', 'depot'] },
  { to: '/procurement/efficiency',      label: 'Efficiency',      icon: Gauge,           roles: ['purchaser', 'hod'] },
  { to: '/procurement/approvals',       label: 'Approvals',       icon: ShieldCheck,     roles: ['hod'] },
  { to: '/procurement/vendors',         label: 'Vendors',         icon: UsersIcon,       roles: ['purchaser'] },
  // Porta Cabin is hidden from the Purchase sidebar — it has its own realm
  // (porta.crystalgrp.xyz) and its own login. The /procurement/porta-cabin
  // routes still resolve for anyone holding a direct link or a bookmark; they
  // are simply no longer advertised here.
  { to: '/procurement/replenishment',   label: 'IMS Replenish',   icon: Boxes,           roles: ['purchaser'] },
  { to: '/procurement/vehicle-placement', label: 'Vehicle Placement', icon: Truck,       roles: ['purchaser', 'logistics'] },
  { to: '/procurement/client-orders',     label: 'Client Orders',     icon: ClipboardList, roles: ['purchaser', 'hod', 'accounts', 'logistics', 'depot'] },
  { to: '/procurement/monday-report',   label: 'Monday Report',   icon: CalendarDays,    roles: ['purchaser', 'hod', 'accounts', 'logistics', 'depot'] },
  { to: '/procurement/search',          label: 'Search',          icon: Search,          roles: ['purchaser', 'hod', 'accounts', 'logistics', 'depot'] },
  { to: '/procurement/pnl',             label: 'P&L',             icon: IndianRupee,     roles: [] },
  { to: '/procurement/team',            label: 'Team & Access',   icon: UsersIcon,       roles: [] },
];

export default function ProcurementLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const location = useLocation();
  // A superadmin and a global admin own this realm from outside it — neither
  // necessarily holds a procurementRole, and both must still reach Team &
  // Access. Without this the one account able to restore anyone's access
  // could not open the page that does it.
  const isAdmin = user?.procurementRole === 'admin'
    || (user as any)?.superAdmin === true
    || user?.role === 'admin';
  // Sidebar visibility: admins see all; Team & Access is admin-only; every other
  // page respects per-member allowedPages grants (falling back to legacy
  // role-based visibility when a member has no explicit grants set).
  /**
   * Efficiency is restricted while it beds in, and the API enforces the same
   * rule. A procurement admin otherwise sees every link — which would put a
   * link in the sidebar that answers 403. A link you are not allowed to use is
   * worse than no link, so this is checked BEFORE the admin bypass.
   */
  const mayEfficiency = user?.role === 'admin'
    || (user as any)?.master === true
    || (Array.isArray(user?.allowedPages) && user!.allowedPages!.includes('efficiency'));

  /** The one shape every page/link check reads, so they cannot disagree. */
  const access = {
    procurementRole: user?.procurementRole,
    allowedPages: user?.allowedPages,
    superAdmin: (user as any)?.superAdmin === true || user?.role === 'admin',
  };

  const links = NAV.filter((l) => {
    if (l.to === '/procurement/efficiency') return mayEfficiency;
    if (l.to === '/procurement/team') return isAdmin && can(user, 'team.manage');
    /**
     * P&L is restricted to named people by EMAIL, not by role — every admin
     * would pass a role check, and there is a second admin-level account that
     * must not see it. The SERVER enforces the same list and 404s everyone
     * else; this only decides whether the link is drawn.
     */
    if (l.to === '/procurement/pnl') return PNL_EMAILS.includes(String(user?.email || '').trim().toLowerCase());
    const page = PROC_PAGE_BY_PATH[l.to];
    // An explicit page list applies to ADMINS TOO — that is the whole point of
    // "manage what each admin sees". `isProcPageAllowed` already returns true
    // for an admin with no explicit list, so the old `if (isAdmin) return true`
    // short-circuit here did nothing but silently ignore the grants an admin
    // had been given (Krishna kept seeing Client Orders after it was removed).
    if (page) return isProcPageAllowed(access, page);
    // Links outside the grantable catalog keep the old role rule.
    if (isAdmin) return true;
    return !!(user?.procurementRole && l.roles.includes(user.procurementRole as any));
  });

  /**
   * Deep-link guard. Hiding a sidebar link is presentation; this is what stops
   * someone typing the URL of a page they were not granted.
   *
   * Done here, once, rather than by wrapping each of the 25 routes: a route
   * added later is covered automatically instead of being covered only if
   * whoever added it remembered. The API enforces the same rules
   * independently — this is the courtesy, not the wall.
   *
   * Matching is longest-prefix so detail routes inherit their list's grant
   * (`/procurement/indents/DOM-0001` follows `proc:indents`). `/procurement`
   * itself is matched EXACTLY: as a prefix it swallows every other page,
   * including Team & Access, which is not in the grantable catalog at all.
   */
  const currentPage = useMemo(() => {
    const path = location.pathname.replace(/\/+$/, '') || '/procurement';
    let best: typeof PROC_PAGES[number] | null = null;
    for (const page of PROC_PAGES) {
      const exact = path === page.path;
      const under = page.path !== '/procurement' && path.startsWith(page.path + '/');
      if ((exact || under) && (!best || page.path.length > best.path.length)) best = page;
    }
    return best;
  }, [location.pathname]);

  const deniedPage = !!currentPage && !isProcPageAllowed(access, currentPage);
  /** Somewhere they CAN go — their first granted link, else the dashboard. */
  const firstAllowed = links[0]?.to || '/procurement';

  // Live count of Sales enquiries still awaiting a purchase reply — drives the
  // red sidebar badge. Only polled for users who can see the Sales Enquiries
  // link (purchaser + admin) so other desks don't hit the endpoint.
  const canSeeEnquiries = links.some((l) => l.to === '/procurement/sales-enquiries');
  const { data: enquiryCount } = useQuery({
    queryKey: ['procurement', 'sales-enquiries', 'unattended'],
    queryFn: () => api.get<{ count: number }>('/api/procurement/sales-enquiries/unattended-count'),
    enabled: !!canSeeEnquiries,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const unattendedEnquiries = enquiryCount?.count ?? 0;

  // Live count of replenishment requests awaiting action — mirrors the Sales
  // Enquiries badge so "always maintain inventory level" stays visible from
  // anywhere in the app, not just when the desk itself is open.
  const canSeeReplenishment = links.some((l) => l.to === '/procurement/replenishment');
  const { data: replenishStats } = useQuery({
    queryKey: ['procurement', 'replenishment', 'stats'],
    queryFn: () => api.get<{ pending: number }>('/api/procurement/replenishment/stats'),
    enabled: !!canSeeReplenishment,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const pendingReplenishment = replenishStats?.pending ?? 0;

  const onLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="procurement-skin min-h-screen bg-slate-50 text-slate-900 flex">
      {/* Mobile menu toggle */}
      <button
        type="button"
        className="lg:hidden fixed top-3 left-3 z-40 h-9 w-9 flex items-center justify-center rounded-md bg-slate-900 text-white shadow-lg"
        onClick={() => setOpen((v) => !v)}
        aria-label="Toggle navigation"
      >
        {open ? <X size={18} /> : <Menu size={18} />}
      </button>

      {/* Drawer backdrop — tap anywhere off the sidebar to close it on a phone. */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-20 bg-slate-900/50"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          // Flex column so the footer sits after the nav instead of being
          // absolutely pinned — on a short phone an absolute footer overlapped
          // the last nav rows. The nav is the only scrolling part.
          // `lg:sticky` LOOKED right but silently failed: `html,body { overflow-x:
          // hidden }` makes an ancestor a scroll container, and position:sticky
          // resolves against that — so the rail scrolled away with the page.
          // Plain `fixed` is immune; `<main>` offsets with lg:ml-60.
          // In-flow sticky rail, not `fixed`: a fixed rail needs <main> to carry a
          // matching margin, and if those two ever disagree the sidebar lands on top
          // of the content. In flow it occupies its own width, so overlap is
          // impossible. (Safe now that html/body use overflow-x: clip, not hidden —
          // `hidden` makes a scroll container and silently breaks sticky.)
          'fixed left-0 top-0 z-30 flex h-screen w-60 max-w-[80vw] shrink-0 flex-col bg-slate-900 text-slate-100 transition-transform',
          'lg:sticky lg:max-w-none',
          open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div className="shrink-0 px-4 py-5 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <BrandMark size={30} />
            <div className="min-w-0">
              <div className="font-mono text-[9px] uppercase tracking-[2px] text-amber-500">Crystal Group</div>
              <div className="font-display text-lg font-extrabold leading-tight text-white">Procurement</div>
            </div>
          </div>
          <div className="text-[10px] text-slate-500 mt-1.5 truncate" title={user?.email}>{user?.email}</div>
          {user?.procurementRole && (
            <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-navy-300">
              {user.procurementRole}
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-3 space-y-0.5">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/procurement'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-4 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-teal-500/15 text-teal-200 border-r-2 border-teal-400'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white',
                  )
                }
                onClick={() => setOpen(false)}
              >
                <Icon size={16} />
                <span>{l.label}</span>
                {l.to === '/procurement/sales-enquiries' && unattendedEnquiries > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold rounded-full bg-red-500 text-white">
                    {unattendedEnquiries}
                  </span>
                )}
                {l.to === '/procurement/replenishment' && pendingReplenishment > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold rounded-full bg-red-500 text-white">
                    {pendingReplenishment}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="shrink-0 border-t border-slate-800 p-3 space-y-1">
          <SwitchRealmButton variant="dark" />
          {isAdmin && (
            <NavLink
              to="/procurement/settings"
              className={({ isActive }) =>
                cn(
                  // DS dark-theme nav: soft-accent fill + lifted navy text.
                  'flex items-center gap-2 rounded-sm px-3 py-2 font-display text-xs font-semibold transition-all duration-fast ease-out',
                  isActive
                    ? 'bg-navy-400/[0.17] text-navy-300'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white',
                )
              }
            >
              <SettingsIcon size={14} /> Settings
            </NavLink>
          )}
          <button
            type="button"
            onClick={onLogout}
            className="flex items-center gap-2 w-full px-3 py-2 rounded text-xs text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main content — the extra top pad on mobile clears the fixed hamburger. */}
      <main className="min-w-0 max-w-full flex-1 [overflow-x:clip] p-3 pt-14 sm:p-4 sm:pt-14 lg:p-6 lg:pt-6">
        {deniedPage ? <Navigate to={firstAllowed} replace /> : <Outlet />}
        {/* Two-hour reply promise — pops up on every Purchase page for whoever
            answers Sales enquiries, until they are answered. */}
        {canSeeEnquiries && <EnquiryReminderPopup />}
      </main>

      {/* Global Indent Control-Tower modal — fires whenever any page calls
          useIndentModalStore.getState().open(indentId). Mirrors the legacy
          Apps Script #indentModal singleton. */}
      <IndentDetailModal />
    </div>
  );
}
